/**
 * StatusPageDashboard.tsx — Status Page dashboard (/dashboard/status)
 *
 * Purpose:    Manage public status page components, incident history, and subscribers.
 *             Includes drag-drop-free component status editor, incident timeline,
 *             subscriber list, and public page URL embed link.
 * Inputs:     useStatusPageData hook (port 3832)
 * Outputs:    ComponentStatusEditor + IncidentTimeline + SubscriberList + embed button
 * Constraints: 7-state AsyncScreen; no external drag-drop deps
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useState } from 'react'
import { AsyncScreen } from '../../components/AsyncScreen'
import {
  useStatusPageData,
  type StatusComponent,
  type ComponentStatus,
  type StatusIncident,
} from '../../hooks/useStatusPageData'

// ---------------------------------------------------------------------------
// Status colours
// ---------------------------------------------------------------------------

const COMPONENT_STATUS_COLORS: Record<ComponentStatus, string> = {
  operational: 'text-green-700 dark:text-green-400',
  degraded: 'text-yellow-700 dark:text-yellow-400',
  partial_outage: 'text-orange-700 dark:text-orange-400',
  major_outage: 'text-red-700 dark:text-red-400',
  maintenance: 'text-sky-700 dark:text-sky-400',
}

const INCIDENT_STATUS_BADGE: Record<StatusIncident['status'], string> = {
  investigating: 'bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  identified: 'bg-orange-50 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  monitoring: 'bg-yellow-50 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  resolved: 'bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300',
}

// ---------------------------------------------------------------------------
// Component status editor row
// ---------------------------------------------------------------------------

function ComponentRow({
  component,
  onStatusChange,
}: {
  component: StatusComponent
  onStatusChange: (id: string, status: ComponentStatus) => Promise<boolean>
}) {
  const [saving, setSaving] = useState(false)

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSaving(true)
    await onStatusChange(component.id, e.target.value as ComponentStatus)
    setSaving(false)
  }

  return (
    <div className="flex items-center justify-between rounded-md border border-brand-subtle bg-brand-surface/50 px-4 py-3">
      <span className="font-medium text-brand-ink">{component.name}</span>
      <div className="flex items-center gap-3">
        <span className={`text-sm ${COMPONENT_STATUS_COLORS[component.status]}`}>
          {component.status.replace('_', ' ')}
        </span>
        <select
          value={component.status}
          onChange={(e) => { void handleChange(e) }}
          disabled={saving}
          className="rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-sm text-brand-ink focus:border-sky-500 focus:outline-none disabled:opacity-50"
          aria-label={`Change status for ${component.name}`}
        >
          <option value="operational">Operational</option>
          <option value="degraded">Degraded</option>
          <option value="partial_outage">Partial outage</option>
          <option value="major_outage">Major outage</option>
          <option value="maintenance">Maintenance</option>
        </select>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Incident timeline
// ---------------------------------------------------------------------------

function IncidentTimeline({ incidents }: { incidents: StatusIncident[] }) {
  if (incidents.length === 0)
    return <p className="text-sm text-brand-muted">No incidents in the last 90 days.</p>

  return (
    <ol className="space-y-3">
      {incidents.map((inc) => (
        <li key={inc.id} className="rounded-lg border border-brand-subtle bg-brand-surface/50 p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className={`mr-2 rounded px-1.5 py-0.5 text-xs ${INCIDENT_STATUS_BADGE[inc.status]}`}>
                {inc.status}
              </span>
              <span className="text-sm font-medium text-brand-ink">{inc.title}</span>
            </div>
            <time className="shrink-0 text-xs text-brand-muted">
              {new Date(inc.createdAt).toLocaleDateString()}
            </time>
          </div>
          {inc.updates.length > 0 && (
            <ul className="mt-2 space-y-1">
              {inc.updates.slice(0, 3).map((u, i) => (
                <li key={i} className="text-xs text-brand-muted">
                  {new Date(u.createdAt).toLocaleTimeString()} — {u.message}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  )
}

// ---------------------------------------------------------------------------
// Subscriber list + add-subscriber form
// ---------------------------------------------------------------------------

function SubscriberList({
  subscribers,
  onAdd,
}: {
  subscribers: Array<{ id: string; email: string; subscribedAt: string }>
  onAdd: (email: string) => Promise<boolean>
}) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) {
      setError('Enter a valid email address')
      return
    }
    setAdding(true)
    setError(null)
    const ok = await onAdd(email.trim())
    setAdding(false)
    if (ok) {
      setEmail('')
    } else {
      setError('Failed to add subscriber')
    }
  }

  return (
    <div>
      <form onSubmit={(e) => { void handleAdd(e) }} className="mb-3 flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          className="flex-1 rounded border border-brand-card bg-brand-surface-2 px-3 py-1.5 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
          aria-label="Subscriber email"
        />
        <button
          type="submit"
          disabled={adding}
          className="rounded-md bg-sky-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
        >
          {adding ? 'Adding…' : 'Subscribe'}
        </button>
      </form>
      {error && <p className="mb-2 text-xs text-red-700 dark:text-red-400">{error}</p>}
      {subscribers.length === 0 ? (
        <p className="text-sm text-brand-muted">No subscribers yet.</p>
      ) : (
        <ul className="space-y-1">
          {subscribers.map((s) => (
            <li key={s.id} className="flex justify-between text-sm">
              <span className="text-brand-secondary">{s.email}</span>
              <time className="text-xs text-brand-muted">
                {new Date(s.subscribedAt).toLocaleDateString()}
              </time>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function StatusPageSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-6" aria-hidden>
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-12 rounded-md bg-brand-surface-2" />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function StatusPageDashboard() {
  const { state, data, error, refresh, updateComponentStatus, addSubscriber } = useStatusPageData()
  const [copied, setCopied] = useState(false)

  const handleCopyEmbed = () => {
    if (!data?.publicUrl) return
    void navigator.clipboard.writeText(data.publicUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">Status Page</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Manage your public status page components and subscribers
          </p>
        </div>
        <div className="flex gap-2">
          {data?.publicUrl && (
            <button
              type="button"
              onClick={handleCopyEmbed}
              className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2"
            >
              {copied ? 'Copied!' : 'Copy public URL'}
            </button>
          )}
          <button
            type="button"
            onClick={() => { void refresh() }}
            className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2"
          >
            Refresh
          </button>
        </div>
      </div>

      <AsyncScreen
        state={state}
        error={error}
        onRetry={() => { void refresh() }}
        loadingSkeleton={<StatusPageSkeleton />}
        emptyMessage="Status page not configured"
      >
        {data && (
          <div className="space-y-8">
            <section>
              <h2 className="mb-3 text-sm font-semibold text-brand-secondary uppercase tracking-wide">
                Components
              </h2>
              <div className="space-y-2">
                {data.components.map((c) => (
                  <ComponentRow
                    key={c.id}
                    component={c}
                    onStatusChange={updateComponentStatus}
                  />
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold text-brand-secondary uppercase tracking-wide">
                Incident history
              </h2>
              <IncidentTimeline incidents={data.incidents} />
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold text-brand-secondary uppercase tracking-wide">
                Subscribers
              </h2>
              <SubscriberList subscribers={data.subscribers} onAdd={addSubscriber} />
            </section>
          </div>
        )}
      </AsyncScreen>
    </div>
  )
}
